export type RequestStatus =
  | "PENDING"
  | "IN_REVIEW"
  | "MATCHED"
  | "EXPIRED"
  | "CANCELLED"
  | "REJECTED";

export type RequestEndReason =
  | "DEADLINE"
  | "CAPACITY_EXHAUSTED"
  | "PAYMENT_TIMEOUT"
  | "CANCELLED_BY_USER"
  | "REJECTED_BY_ADMIN";

export type LeadStatus = "LEAD" | "AWAITING_PAYMENT" | "MATCHED";

export interface CuratedRequest {
  id: string;
  status: RequestStatus;
  criteria?: Record<string, unknown>;
  adminNotes?: string | null;
  matchId?: string | null;
  expiresAt?: string | null;
  endReason?: RequestEndReason | null;
  createdAt: string;
  updatedAt: string;
}

export interface LeadCandidate {
  id: string;
  name: string;
  age?: number | null;
  occupation?: string | null;
  bio?: string | null;
  interests?: string[] | null;
  photoUrl?: string | null;
}

export interface Lead {
  id: string;
  requestId: string;
  status: LeadStatus;
  respondBy?: string | null;
  paymentDueAt?: string | null;
  price: number;
  currency: string;
  matchId?: string | null;
  candidate: LeadCandidate;
}

export interface MyRequestPayload {
  request: CuratedRequest | null;
  activeLead: Lead | null;
  leadCapacity: number;
  leadsUsedThisMonth: number;
  leadsRemaining: number;
  capacityResetsAt: string;
}

/** Channels accepted by the curated-lead checkout */
export type LeadChannel =
  | "card"
  | "bank_transfer"
  | "mpamba"
  | "airtel_money"
  | "paychangu_ussd"
  | "paychangu_airtel_money"
  | "paychangu_mpamba";

export interface LeadCheckoutBody {
  channel: LeadChannel;
  msisdn?: string;
  mobileMoneyOperator?: "tnm" | "airtel";
  email?: string;
}

export interface LeadCheckoutResponse {
  outreachId: string;
  reference: string;
  paymentId: string;
  amount: number;
  currency: string;
  channel: string;
  paymentStatus: "PROCESSING" | "PENDING" | "COMPLETED" | "FAILED";
  requiresRedirect?: boolean;
  checkoutUrl?: string;
  paymentDueAt: string;
  wsUrl?: string;
  wsExpiresInSeconds?: number;
  instruction?: string;
}

export interface LeadVerifyResponse {
  reference: string;
  status: "COMPLETED" | "PENDING" | "FAILED";
  matchId?: string;
}

/** Thrown by the service when the backend refuses with a code */
export class ApiError extends Error {
  code: string;
  constructor(message: string, code = "UNKNOWN") {
    super(message);
    this.name = "ApiError";
    this.code = code;
  }
}
